import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaLock, FaKey, FaWifi } from 'react-icons/fa';
import BrandLogo from '../components/BrandLogo';
import { hasPin, getDeviceProfile, setPin as saveDevicePin } from '../utils/deviceAuth';

type Mode = 'password' | 'pin' | 'setPin';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [online, setOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);

  const deviceHasPin = hasPin();
  const deviceEmail = getDeviceProfile()?.email || '';
  // Si no hay internet y este equipo ya tiene PIN, arranca directo en modo PIN.
  const [mode, setMode] = useState<Mode>(!online && deviceHasPin ? 'pin' : 'password');
  const [pin, setPinInput] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');

  const { login, unlockOffline } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('sessionExpired')) {
      setSessionExpired(true);
      localStorage.removeItem('sessionExpired');
    }
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Login normal (con internet)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      await login({ email, password });
      // Primera vez en este equipo sin PIN → ofrecer crearlo para entrar offline
      if (!hasPin()) { setIsLoading(false); setMode('setPin'); return; }
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  // Entrar sin internet con el PIN del dispositivo
  const handlePinLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      const ok = await unlockOffline(pin);
      if (ok) navigate('/');
      else setError('PIN incorrecto');
    } catch {
      setError('No se pudo validar el PIN');
    } finally { setIsLoading(false); }
  };

  // Crear el PIN tras el primer login online
  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pin.length < 4) { setError('El PIN debe tener al menos 4 dígitos'); return; }
    if (pin !== pinConfirm) { setError('Los PIN no coinciden'); return; }
    setIsLoading(true);
    await saveDevicePin(pin);
    setIsLoading(false);
    navigate('/');
  };

  const cardTitle = mode === 'pin' ? 'Entrar con PIN' : mode === 'setPin' ? 'Crea tu PIN' : 'Panel de administración';

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="card shadow-sm" style={{ maxWidth: '400px', width: '100%' }}>
        <div className="card-body p-4">
          <div className="text-center mb-3">
            <BrandLogo size={52} />
          </div>
          <p className="text-center text-muted mb-3" style={{ fontSize: 14 }}>
            {cardTitle}
            {process.env.REACT_APP_VERSION && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>v{process.env.REACT_APP_VERSION}</span>
            )}
          </p>

          {!online && (
            <div className="alert alert-warning d-flex align-items-center gap-2 py-2" role="alert" style={{ fontSize: 13 }}>
              <FaWifi /> Sin conexión — modo offline
            </div>
          )}
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {sessionExpired && mode === 'password' && (
            <div className="alert alert-warning" role="alert">Tu sesión expiró. Inicia sesión nuevamente{deviceHasPin ? ' o entra con tu PIN' : ''}.</div>
          )}

          {/* ── Modo contraseña (online) ── */}
          {mode === 'password' && (
            <>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label"><FaUser className="me-2" />Correo Electrónico</label>
                  <input type="email" className="form-control" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" disabled={isLoading} />
                </div>
                <div className="mb-4">
                  <label htmlFor="password" className="form-label"><FaLock className="me-2" />Contraseña</label>
                  <input type="password" className="form-control" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" disabled={isLoading} />
                </div>
                <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                  {isLoading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Iniciando sesión...</>) : 'Iniciar Sesión'}
                </button>
              </form>
              {deviceHasPin && (
                <button type="button" className="btn btn-link w-100 mt-2" style={{ fontSize: 13, textDecoration: 'none' }} onClick={() => { setError(''); setPinInput(''); setMode('pin'); }}>
                  <FaKey className="me-1" /> Entrar con PIN{!online ? ' (sin internet)' : ''}
                </button>
              )}
            </>
          )}

          {/* ── Modo PIN (offline) ── */}
          {mode === 'pin' && (
            <form onSubmit={handlePinLogin}>
              {deviceEmail && <div className="text-center text-muted mb-3" style={{ fontSize: 13 }}>{deviceEmail}</div>}
              <div className="mb-4">
                <label htmlFor="pin" className="form-label"><FaKey className="me-2" />PIN</label>
                <input type="password" inputMode="numeric" className="form-control text-center" id="pin" value={pin} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 8))} required autoFocus disabled={isLoading} style={{ letterSpacing: 8, fontSize: 22 }} />
              </div>
              <button type="submit" className="btn btn-primary w-100" disabled={isLoading || pin.length < 4}>
                {isLoading ? 'Validando...' : 'Entrar'}
              </button>
              <button type="button" className="btn btn-link w-100 mt-2" style={{ fontSize: 13, textDecoration: 'none' }} onClick={() => { setError(''); setMode('password'); }}>
                Usar correo y contraseña
              </button>
            </form>
          )}

          {/* ── Crear PIN (tras el primer login online) ── */}
          {mode === 'setPin' && (
            <form onSubmit={handleSetPin}>
              <p className="text-muted" style={{ fontSize: 13 }}>Crea un PIN para poder entrar y vender <b>sin internet</b> en este dispositivo.</p>
              <div className="mb-3">
                <label className="form-label"><FaKey className="me-2" />PIN (4 a 8 dígitos)</label>
                <input type="password" inputMode="numeric" className="form-control text-center" value={pin} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 8))} required autoFocus style={{ letterSpacing: 8, fontSize: 22 }} />
              </div>
              <div className="mb-4">
                <label className="form-label">Repite el PIN</label>
                <input type="password" inputMode="numeric" className="form-control text-center" value={pinConfirm} onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 8))} required style={{ letterSpacing: 8, fontSize: 22 }} />
              </div>
              <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>{isLoading ? 'Guardando...' : 'Guardar PIN y entrar'}</button>
              <button type="button" className="btn btn-link w-100 mt-2" style={{ fontSize: 13, textDecoration: 'none' }} onClick={() => navigate('/')}>Omitir por ahora</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
